import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { MoodPill, EmotionTag } from "@/components/MoodTag";
import ReflectionCard from "@/components/ReflectionCard";
import { ArrowLeft, Trash, CalendarBlank } from "@phosphor-icons/react";

export default function EntryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/journal/entries/${id}`);
        setEntry(data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [id]);

  const onDelete = async () => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/journal/entries/${id}`);
      navigate("/app", { replace: true });
    } catch (e) {
      setError(formatApiError(e));
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone-100" data-testid="entry-detail-page">
      <header
        className="sticky top-0 z-20 bg-bone-100/75 backdrop-blur-xl border-b border-bone-300"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link
            to="/app"
            className="flex items-center gap-2 text-ink-700 hover:text-ink-900"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Back to journal</span>
          </Link>
          <button
            onClick={onDelete}
            disabled={deleting}
            className="text-ink-500 hover:text-brand p-2 rounded-full hover:bg-bone-200 disabled:opacity-50"
            data-testid="delete-entry-btn"
            aria-label="Delete entry"
          >
            <Trash size={18} />
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12">
        {error && (
          <div className="text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2 mb-6">
            {error}
          </div>
        )}
        {entry && (
          <>
            <p className="eyebrow flex items-center gap-2">
              <CalendarBlank size={14} />
              {new Date(entry.created_at).toLocaleString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <MoodPill mood={entry.mood} score={entry.sentiment_score} />
              {(entry.emotion_tags || []).map((t) => (
                <EmotionTag key={t} tag={t} />
              ))}
            </div>
            <article className="mt-10 font-serif text-2xl sm:text-3xl text-ink-900 leading-[1.55] whitespace-pre-wrap">
              {entry.transcript}
            </article>
            {entry.reflection && (
              <div className="mt-10">
                <ReflectionCard
                  reflection={entry.reflection}
                  variant="detail"
                  entryId={entry.entry_id}
                  initialRating={entry.reflection_rating}
                />
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
